import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Building2, User } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "@/components/ui/Card";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CourtRecord {
  id: number;
  agency_id: number;
  name: string;
}

interface JudgeRecord {
  id: number;
  agency_id: number;
  name: string;
  court_id: number | null;
  court_name?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CourtsJudgesAdmin() {
  const queryClient = useQueryClient();
  const [showCourtForm, setShowCourtForm] = useState(false);
  const [showJudgeForm, setShowJudgeForm] = useState(false);
  const [newCourtName, setNewCourtName] = useState("");
  const [newJudgeName, setNewJudgeName] = useState("");
  const [newJudgeCourtId, setNewJudgeCourtId] = useState("");

  const { data: courtsData, isLoading: courtsLoading } = useQuery<{
    data: CourtRecord[];
  }>({
    queryKey: ["courts"],
    queryFn: () => api.get<{ data: CourtRecord[] }>("/legal/courts"),
  });

  const { data: judgesData, isLoading: judgesLoading } = useQuery<{
    data: JudgeRecord[];
  }>({
    queryKey: ["judges"],
    queryFn: () => api.get<{ data: JudgeRecord[] }>("/legal/judges"),
  });

  const createCourtMutation = useMutation({
    mutationFn: (data: { name: string }) =>
      api.post("/legal/courts", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["courts"] });
      setShowCourtForm(false);
      setNewCourtName("");
    },
  });

  const createJudgeMutation = useMutation({
    mutationFn: (data: { name: string; courtId?: number }) =>
      api.post("/legal/judges", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["judges"] });
      setShowJudgeForm(false);
      setNewJudgeName("");
      setNewJudgeCourtId("");
    },
  });

  const courts = courtsData?.data ?? [];
  const judges = judgesData?.data ?? [];

  const courtOptions = courts.map((c) => ({
    value: String(c.id),
    label: c.name,
  }));

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-foreground">
        Courts & Judges Administration
      </h1>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Courts */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-base">
                <Building2 className="h-5 w-5" />
                Courts
              </CardTitle>
              <Button
                size="sm"
                className="gap-2"
                onClick={() => setShowCourtForm(true)}
              >
                <Plus className="h-4 w-4" />
                Add Court
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {showCourtForm && (
              <div className="mb-4 space-y-3 rounded-md border p-3">
                <Input
                  label="Court Name"
                  value={newCourtName}
                  onChange={(e) => setNewCourtName(e.target.value)}
                  placeholder="Enter court name..."
                  required
                />
                <div className="flex gap-3">
                  <Button
                    size="sm"
                    onClick={() =>
                      createCourtMutation.mutate({ name: newCourtName })
                    }
                    loading={createCourtMutation.isPending}
                    disabled={!newCourtName.trim()}
                  >
                    Create
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setShowCourtForm(false);
                      setNewCourtName("");
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {courtsLoading && (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="h-10 animate-pulse rounded bg-muted"
                  />
                ))}
              </div>
            )}

            {!courtsLoading && courts.length === 0 && (
              <p className="py-6 text-center text-sm text-muted-foreground">
                No courts configured.
              </p>
            )}

            {!courtsLoading && courts.length > 0 && (
              <div className="space-y-2">
                {courts.map((court) => (
                  <div
                    key={court.id}
                    className="flex items-center justify-between rounded-md border px-3 py-2"
                  >
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-medium">{court.name}</span>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      ID: {court.id}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Judges */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-base">
                <User className="h-5 w-5" />
                Judges
              </CardTitle>
              <Button
                size="sm"
                className="gap-2"
                onClick={() => setShowJudgeForm(true)}
              >
                <Plus className="h-4 w-4" />
                Add Judge
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {showJudgeForm && (
              <div className="mb-4 space-y-3 rounded-md border p-3">
                <Input
                  label="Judge Name"
                  value={newJudgeName}
                  onChange={(e) => setNewJudgeName(e.target.value)}
                  placeholder="Enter judge name..."
                  required
                />
                <Select
                  label="Court (optional)"
                  options={courtOptions}
                  value={newJudgeCourtId}
                  onChange={setNewJudgeCourtId}
                  placeholder="Select court..."
                  clearable
                  searchable
                />
                <div className="flex gap-3">
                  <Button
                    size="sm"
                    onClick={() =>
                      createJudgeMutation.mutate({
                        name: newJudgeName,
                        courtId: newJudgeCourtId
                          ? parseInt(newJudgeCourtId, 10)
                          : undefined,
                      })
                    }
                    loading={createJudgeMutation.isPending}
                    disabled={!newJudgeName.trim()}
                  >
                    Create
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setShowJudgeForm(false);
                      setNewJudgeName("");
                      setNewJudgeCourtId("");
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {judgesLoading && (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="h-10 animate-pulse rounded bg-muted"
                  />
                ))}
              </div>
            )}

            {!judgesLoading && judges.length === 0 && (
              <p className="py-6 text-center text-sm text-muted-foreground">
                No judges configured.
              </p>
            )}

            {!judgesLoading && judges.length > 0 && (
              <div className="space-y-2">
                {judges.map((judge) => (
                  <div
                    key={judge.id}
                    className="flex items-center justify-between rounded-md border px-3 py-2"
                  >
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <span className="text-sm font-medium">
                          {judge.name}
                        </span>
                        {judge.court_name && (
                          <span className="ml-2 text-xs text-muted-foreground">
                            ({judge.court_name})
                          </span>
                        )}
                      </div>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      ID: {judge.id}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
